import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuthContext } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { LanguageSelector } from '@/components/common/LanguageSelector';
import { WhatsAppConnectCard } from '@/components/configuracoes/WhatsAppConnectCard';
import { LogoOrganizacaoCard } from '@/components/configuracoes/LogoOrganizacaoCard';
import { MarketplacePerfilCard } from '@/components/configuracoes/MarketplacePerfilCard';
import { useConfiguracoes } from '@/hooks/useConfiguracoes';

interface SectionProps {
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
}

function Section({ titulo, descricao, children }: SectionProps) {
  return (
    <div className="ow-card">
      <div className="px-6 pt-5 pb-3">
        <h3 className="text-base font-bold tracking-tight">{titulo}</h3>
        {descricao && <p className="text-xs text-muted-foreground mt-0.5">{descricao}</p>}
      </div>
      <div className="px-6 pb-5">{children}</div>
    </div>
  );
}

export default function Configuracoes() {
  const { t } = useTranslation();
  const { isAdmin } = useAuthContext();
  const { isLoading } = useConfiguracoes();

  if (!isAdmin) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center h-[60vh]">
          <EmptyState
            titulo={t('settings.restrictedAccess')}
            descricao={t('settings.restrictedDescription')}
          />
        </div>
      </MainLayout>
    );
  }

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto flex flex-col gap-5">
        <PageHeader titulo={t('settings.title')} />

        <LogoOrganizacaoCard />

        <MarketplacePerfilCard />

        <Section titulo={t('settings.language')} descricao={t('settings.languageDesc')}>
          <LanguageSelector variant="full" />
        </Section>

        <WhatsAppConnectCard />
      </div>
    </MainLayout>
  );
}
